import { IPlanDownloadRequest } from '../Models/Requests/PlanRequest';
import { downloadCsiPdf, ICsiRequest } from '../store/CSI/CsiThunks';
import { downloadPlanPdf, downloadMaxscriptPdf } from '../store/Grower/PlanThunks';
import { downloadVRSPdf, IPlantingPlanPdfRequest } from '../store/Grower/PlantingPlanThunks';
import { downloadPpgPdf, IPpgRequest } from '../store/PPG/PpgThunks';
import type { AppDispatch } from '../store/Store';
import { ACWithReason } from './anySignal';

type Inflight = {
  promise: any;                 // RTK thunk promise
  controller: AbortController;  // our controller carrying the reason
};

const inflight = new Map<string, Inflight>();  // requestId -> { promise, controller }

const supportsReason =
  typeof AbortSignal !== 'undefined' && 'reason' in (AbortSignal.prototype as any);

// For older DOM libs/runtimes without AbortSignal.reason
const abortReasonFallback = new Map<string, any>();
export const getAbortReasonFallback = (requestId: string) => abortReasonFallback.get(requestId);

export const cancelById = (id: string, reason?: any) =>
{
	const entry = inflight.get(id);
	if (!entry)
	{
		// (turn on for debug)
		//console.warn('[cancelById] no inflight entry for id', id);
		return;
	}

	if (supportsReason)
	{
		(entry.controller as ACWithReason).abort(reason);
	}
	else
	{
		abortReasonFallback.set(id, reason);
		entry.controller.abort();
	}

	// Also abort RTK’s internal signal (nice to have)
	entry.promise?.abort?.(reason);
};

export const startPlanPdf = (args: IPlanDownloadRequest) => (dispatch: AppDispatch) =>
{
	// 1) make our controller & pass its signal into the thunk args
	const controller = new AbortController();
	const p = dispatch(
		downloadPlanPdf({ ...args, __externalSignal: controller.signal } as any)
	) as any;

	// 2) REGISTER the inflight entry using the thunk promise's requestId
	inflight.set(p.requestId, { promise: p, controller });

	// 3) clean up when the thunk resolves/rejects/aborts
	p.finally(() =>
	{
		inflight.delete(p.requestId);
		abortReasonFallback.delete(p.requestId);
	});

	return p;
};

export const startMaxscriptPdf = () => (dispatch: AppDispatch) =>
{
	// 1) make our controller & pass its signal into the thunk args
	const controller = new AbortController();
	const p = dispatch(
		downloadMaxscriptPdf({ __externalSignal: controller.signal } as any)
	) as any;

	// 2) REGISTER the inflight entry using the thunk promise's requestId
	inflight.set(p.requestId, { promise: p, controller });

	// 3) clean up when the thunk resolves/rejects/aborts
	p.finally(() =>
	{
		inflight.delete(p.requestId);
		abortReasonFallback.delete(p.requestId);
	});

	return p;
};

export const startVrsPdf = (args: IPlantingPlanPdfRequest) => (dispatch: AppDispatch) =>
{
	// 1) make our controller & pass its signal into the thunk args
	const controller = new AbortController();
	const p = dispatch(
		downloadVRSPdf({ ...args, __externalSignal: controller.signal } as any)
	) as any;

	// 2) REGISTER the inflight entry using the thunk promise's requestId
	inflight.set(p.requestId, { promise: p, controller });

	// 3) clean up when the thunk resolves/rejects/aborts
	p.finally(() =>
	{
		inflight.delete(p.requestId);
		abortReasonFallback.delete(p.requestId);
	});

	return p;
};

export const startCsiPdf = (args: ICsiRequest) => (dispatch: AppDispatch) =>
{
	// 1) make our controller & pass its signal into the thunk args
	const controller = new AbortController();
	const p = dispatch(
		downloadCsiPdf({ ...args, __externalSignal: controller.signal } as any)
	) as any;

	// 2) REGISTER the inflight entry using the thunk promise's requestId
	inflight.set(p.requestId, { promise: p, controller });

	// 3) clean up when the thunk resolves/rejects/aborts
	p.finally(() =>
	{
		inflight.delete(p.requestId);
		abortReasonFallback.delete(p.requestId);
	});

	return p;
};

export const startPpgPdf = (args: IPpgRequest) => (dispatch: AppDispatch) =>
{
	// 1) make our controller & pass its signal into the thunk args
	const controller = new AbortController();
	const p = dispatch(
		downloadPpgPdf({ ...args, __externalSignal: controller.signal } as any)
	) as any;

	// 2) REGISTER the inflight entry using the thunk promise's requestId
	inflight.set(p.requestId, { promise: p, controller });

	// 3) clean up when the thunk resolves/rejects/aborts
	p.finally(() =>
	{
		inflight.delete(p.requestId);
		abortReasonFallback.delete(p.requestId);
	});

	return p;
};